import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, findCategory } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { platforms } from "@/mocks/platforms";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = findCategory(slug ?? "");
  const fallback = categories[0];
  const current = category ?? fallback;

  usePageMeta(
    `${current.name}韩漫分类 - 正版韩漫大全`,
    `${current.name}韩漫正版分类页，${current.tagline}，汇总官方正版作品入口与追更指南。`
  );

  const recommend = articles.slice(0, 3);

  return (
    <div className="bg-background-50">
      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src={current.image}
          alt={`${current.name}韩漫分类封面`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <nav className="text-xs text-background-100/70" aria-label="面包屑">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-background-50">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/#categories" className="hover:text-background-50">漫画分类</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-background-50" aria-current="page">{current.name}</li>
            </ol>
          </nav>
          <div className="mt-6 max-w-2xl">
            <span className={`inline-flex h-11 w-11 items-center justify-center rounded-md ${current.color} text-background-50`}>
              <i className={`text-xl ${current.icon}`}></i>
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-4xl">{current.name}</h1>
            <p className="mt-2 font-comic text-secondary-300">{current.tagline}</p>
            <p className="mt-4 text-sm leading-relaxed text-background-100/90">{current.desc}</p>
            <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs text-background-100">
              <i className="ri-verified-badge-line text-secondary-300"></i>
              {current.count}+ 部正版作品入口
            </span>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <span className="text-xs font-medium uppercase tracking-widest text-primary-600">分类介绍</span>
          <h2 className="mt-2 font-heading text-xl font-bold text-foreground-900 md:text-2xl">关于「{current.name}」类韩漫</h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            {current.name}类韩漫是正版韩漫大全中最受欢迎的品类之一，拥有庞大的读者群与持续更新的优质作品。
            在这类作品中，你可以体验到独特的叙事节奏与细腻的画面表现，无论是轻松消遣还是深度追更都很合适。
          </p>
          <div className="mt-6 space-y-4">
            {[
              { icon: "ri-scales-3-line", title: "题材定位", desc: `${current.name}类作品题材定位清晰，${current.tagline}，适合对相关元素感兴趣的读者。` },
              { icon: "ri-stack-line", title: "内容体量", desc: `官方正版平台在该品类下持续连载，已收录 ${current.count}+ 部作品入口，更新稳定。` },
              { icon: "ri-magic-line", title: "阅读建议", desc: "建议通过正版韩漫大全整理的官方入口进入，按人气榜与完结筛选快速定位心仪作品。" },
            ].map((item) => (
              <div key={item.title} className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${current.color} text-background-50`}>
                  <i className={`text-lg ${item.icon}`}></i>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h3 className="font-heading text-lg font-bold text-foreground-900">延伸阅读</h3>
            <div className="mt-4 space-y-4">
              {recommend.map((a) => (
                <Link
                  key={a.id}
                  to={`/article/${a.slug}`}
                  className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all hover:border-primary-300"
                >
                  <img
                    src={a.cover}
                    alt={`${a.title}封面`}
                    className="hidden w-32 shrink-0 rounded-md object-cover object-top sm:block"
                  />
                  <div className="py-1">
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span className="text-primary-600">{a.category}</span>
                      <span>·</span>
                      <span>{a.date}</span>
                    </div>
                    <h4 className="mt-1.5 text-sm font-semibold text-foreground-900 group-hover:text-primary-600">{a.title}</h4>
                    <p className="mt-1.5 text-sm text-foreground-600">{a.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="font-heading text-base font-bold text-foreground-900">去官方正版渠道看看</h3>
            <p className="mt-2 text-sm text-foreground-600">认准正版授权平台，追更更安心。</p>
            <div className="mt-4 space-y-3">
              {platforms.slice(0, 4).map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-md bg-background-100 p-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${p.color} text-background-50`}>
                    <i className={`text-base ${p.icon}`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground-900">{p.name}</p>
                    <p className="truncate text-xs text-foreground-500">{p.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/#official"
              className="mt-5 flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部官方入口 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}